import React, { useEffect, useMemo, useState } from "react";
import {
  FaBars, FaTachometerAlt, FaCalendarAlt, FaClipboardList,
  FaUmbrellaBeach, FaSignOutAlt, FaTimes
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import logo from "../assets/urbango-white.webp";
import "bootstrap/dist/css/bootstrap.min.css";

function getProfileImage(employee) {
  if (employee?.imageUrl) return employee.imageUrl;
  if (employee?.employeeNo) {
    const base = "https://hrms.urbango.co.in/server/uploads/employees";
   
    return `${base}/${employee.employeeNo}.jpg`;
  }
  return "https://via.placeholder.com/50";
}

function Sidebar({ setActivePage, activePage, employee }) {
  const [showMenu, setShowMenu] = useState(false);
  const [unread, setUnread] = useState(0);
  const [latestTs, setLatestTs] = useState(() => localStorage.getItem("announce_latest_ts") || "");
  const profilePic = useMemo(() => getProfileImage(employee), [employee]);
  const navigate = useNavigate();

  useEffect(() => {
    let stop = false;
    const fetchCount = () => {
      fetch("https://hrms.urbango.co.in/server/index.php?endpoint=announce_list")
        .then(r => r.json())
        .then(data => {
          if (stop) return;
          const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
          const newest = list.reduce((acc, r) => {
            const t = new Date(r.Date || r.date || 0).getTime();
            return Math.max(acc, isNaN(t) ? 0 : t);
          }, 0);
          if (!newest) return;
          const lastSeen = Number(localStorage.getItem("announce_latest_ts")) || 0;
          setLatestTs(String(newest));
          const count = list.filter(r => {
            const t = new Date(r.Date || r.date || 0).getTime();
            return !isNaN(t) && t > lastSeen;
          }).length;
          if (count !== unread) setUnread(count);
        })
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [unread]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleMenuItemClick = (page) => {
    setActivePage(page);
    setShowMenu(false);
    if (page === "employeeannouncements") {
      if (latestTs) localStorage.setItem("announce_latest_ts", String(latestTs));
      setUnread(0);
    }
  };

  return (
    <>
      {!showMenu && (
        <button
          className="btn btn-light shadow-sm rounded-3 glass-burger d-lg-none"
          aria-label="Open Sidebar Menu"
          onClick={() => setShowMenu(true)}
          type="button"
        >
          <FaBars />
        </button>
      )}

      {/* Desktop persistent sidebar */}
      <aside className="glass-sidebar d-none d-lg-flex flex-column">
        <div className="glass-header position-relative text-center">
          <img src={logo} alt="Company Logo" className="company-logo img-fluid" />
          <div className="employee-info d-flex flex-column align-items-center mt-2">
            <img src={profilePic} alt="Profile" className="profile-pic" />
            <p className="welcome-text mb-0">Welcome, {employee?.name || "Employee"} 👋</p>
          </div>
        </div>

        <ul className="list-unstyled glass-menu flex-grow-1 mt-3">
          <li className={activePage === "dashboard" ? "active" : ""} onClick={() => handleMenuItemClick("dashboard")}>
            <FaTachometerAlt /> <span>Dashboard</span>
          </li>
          <li className={activePage === "attendance" ? "active" : ""} onClick={() => handleMenuItemClick("attendance")}>
            <FaCalendarAlt /> <span>Attendance</span>
          </li>
          <li className={activePage === "leave" ? "active" : ""} onClick={() => handleMenuItemClick("leave")}>
            <FaClipboardList /> <span>Leave Request</span>
          </li>
          <li className={activePage === "holiday" ? "active" : ""} onClick={() => handleMenuItemClick("holiday")}>
            <FaUmbrellaBeach /> <span>Holiday List</span>
          </li>
          <li
            className={activePage === "employeeannouncements" ? "active badge-parent" : "badge-parent"}
            onClick={() => handleMenuItemClick("employeeannouncements")}
          >
            <FaClipboardList /> <span>Announcements</span>
            {unread > 0 && <span className="menu-badge">{unread > 99 ? "99+" : unread}</span>}
          </li>
        </ul>

        <button className="btn btn-link text-decoration-none logout-btn align-self-start" onClick={handleLogout}>
          <FaSignOutAlt /> <span>Logout</span>
        </button>
      </aside>

      {/* Mobile/Tablet offcanvas */}
      <div className={`offcanvas offcanvas-start glass-offcanvas ${showMenu ? "show" : ""}`} style={{ visibility: showMenu ? "visible" : "hidden" }} aria-modal={showMenu ? "true" : "false"} role="dialog">
        <div className="offcanvas-header">
          <img src={logo} alt="Company Logo" className="company-logo img-fluid" />
          <button type="button" aria-label="Close Sidebar" className="btn text-white fs-4" onClick={() => setShowMenu(false)}>
            <FaTimes />
          </button>
        </div>
        <div className="offcanvas-body d-flex flex-column">
          <div className="employee-info d-flex flex-column align-items-center mb-3">
            <img src={profilePic} alt="Profile" className="profile-pic" />
            <p className="welcome-text mb-0">Welcome, {employee?.name || "Employee"} 👋</p>
          </div>

          <ul className="list-unstyled glass-menu flex-grow-1">
            <li className={activePage === "dashboard" ? "active" : ""} onClick={() => handleMenuItemClick("dashboard")}>
              <FaTachometerAlt /> <span>Dashboard</span>
            </li>
            <li className={activePage === "attendance" ? "active" : ""} onClick={() => handleMenuItemClick("attendance")}>
              <FaCalendarAlt /> <span>Attendance</span>
            </li>
            <li className={activePage === "leave" ? "active" : ""} onClick={() => handleMenuItemClick("leave")}>
              <FaClipboardList /> <span>Leave Request</span>
            </li>
            <li className={activePage === "holiday" ? "active" : ""} onClick={() => handleMenuItemClick("holiday")}>
              <FaUmbrellaBeach /> <span>Holiday List</span>
            </li>
            <li
              className={activePage === "employeeannouncements" ? "active badge-parent" : "badge-parent"}
              onClick={() => handleMenuItemClick("employeeannouncements")}
            >
              <FaClipboardList /> <span>Announcements</span>
              {unread > 0 && <span className="menu-badge">{unread > 99 ? "99+" : unread}</span>}
            </li>
          </ul>

          <button className="btn btn-link text-decoration-none logout-btn text-start" onClick={handleLogout}>
            <FaSignOutAlt /> <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Offcanvas backdrop */}
      {showMenu && <div className="offcanvas-backdrop fade show" onClick={() => setShowMenu(false)} />}
    </>
  );
}

export default Sidebar;
